import React, { useEffect, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import type { StatsAggReportItemDefDto } from '../../../types/api';
import { AGG_ITEM_TYPE_OPTIONS } from './aggReportConstants';

interface AggAssessmentOption {
  value: string;
  label: string;
  /** This assessment's data variables (StatsAggVarDto — value = mVar, label = dName). */
  aggVars: Array<{ value: string; label: string }>;
}

interface AggReportItemListEditorProps {
  items: StatsAggReportItemDefDto[];
  onChange: (nextItems: StatsAggReportItemDefDto[]) => void;
  /** The account's enabled PLA assessments, each with its own variable catalog. Each report
   * item picks one of these (the "Assessment" field between Title and Variable) and its
   * Variable/Group By dropdowns are sourced from that assessment's aggVars. */
  assessmentOptions: AggAssessmentOption[];
}

const LISTBOX_VISIBLE_ROWS = 6;
const LISTBOX_ROW_HEIGHT = 18;
const LISTBOX_HEIGHT = LISTBOX_VISIBLE_ROWS * LISTBOX_ROW_HEIGHT;
const LISTBOX_WIDTH = 220;

const webDropdownStyle = { ...webControlStyle, height: 26, minWidth: 170 } as any;
const WEB_LISTBOX_STYLE = {
  width: `${LISTBOX_WIDTH}px`,
  border: `1px solid ${EDITOR_COLORS.controlBorder}`,
  borderRadius: '4px',
  backgroundColor: EDITOR_COLORS.background,
  color: EDITOR_COLORS.textPrimary,
  fontFamily: 'Arial',
  fontSize: '13px',
  lineHeight: '1.35',
  padding: '2px',
  boxSizing: 'border-box',
} as const;

const generateItemId = (): string => `agg_item_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/** Pos is never hand-edited — recomputed as String(index + 1) after every mutation, same
 * convention as AccountSettingsView's Pos-derived-at-build-time fields. */
const withRecomputedPos = (items: StatsAggReportItemDefDto[]): StatsAggReportItemDefDto[] =>
  items.map((item, index) => ({ ...item, Pos: String(index + 1) }));

const itemTypeLabel = (type: string): string => AGG_ITEM_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;

const itemListLabel = (item: StatsAggReportItemDefDto, index: number): string =>
  `${item.DName || `Item ${index + 1}`} — ${itemTypeLabel(item.Type)}`;

const renderFieldDropdown = (
  value: string,
  onChange: (value: string) => void,
  options: Array<{ value: string; label: string }>,
  ariaLabel: string
) => {
  if (Platform.OS === 'web') {
    return React.createElement(
      'select' as any,
      {
        value,
        onChange: (e: any) => onChange(String(e?.target?.value ?? '')),
        style: webDropdownStyle,
        'aria-label': ariaLabel,
      },
      options.map((option) => React.createElement('option' as any, { key: option.value, value: option.value }, option.label))
    );
  }
  return <Text style={styles.formStaticValue}>{options.find((option) => option.value === value)?.label ?? ''}</Text>;
};

export const AggReportItemListEditor: React.FC<AggReportItemListEditorProps> = ({ items, onChange, assessmentOptions }) => {
  const defaultType = AGG_ITEM_TYPE_OPTIONS[0]?.value ?? '';
  const defaultAssessment = assessmentOptions[0]?.value ?? '';

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [formType, setFormType] = useState(defaultType);
  const [formDName, setFormDName] = useState('');
  const [formAssessmentId, setFormAssessmentId] = useState(defaultAssessment);
  const [formVarX, setFormVarX] = useState('');
  const [formVarY, setFormVarY] = useState('');

  const needsVarY = AGG_ITEM_TYPE_OPTIONS.find((o) => o.value === formType)?.needsVarY ?? false;
  const variableOptions = assessmentOptions.find((a) => a.value === formAssessmentId)?.aggVars ?? [];
  const firstVar = variableOptions[0]?.value ?? '';

  // Keep the Assessment / Variable / Group By selections pointing at real options whenever any
  // are available. A controlled <select> whose value matches no <option> lets the browser show
  // its own choice, and a click on that already-shown option then fires no onChange (the same
  // desync as the item listbox) — so a single-option dropdown left on a stale/empty value would
  // never populate the variable lists. Covers the async load of assessmentOptions and an item
  // whose stored AssessmentId/VarX/VarY isn't among the account's currently-enabled options.
  useEffect(() => {
    if (assessmentOptions.length === 0) {
      return;
    }
    const currentAssessment = assessmentOptions.find((a) => a.value === formAssessmentId);
    if (!currentAssessment) {
      const first = assessmentOptions[0];
      const firstV = first.aggVars[0]?.value ?? '';
      setFormAssessmentId(first.value);
      setFormVarX(firstV);
      setFormVarY(firstV);
      return;
    }
    const vars = currentAssessment.aggVars;
    if (vars.length > 0) {
      if (!vars.some((v) => v.value === formVarX)) {
        setFormVarX(vars[0].value);
      }
      if (needsVarY && !vars.some((v) => v.value === formVarY)) {
        setFormVarY(vars[0].value);
      }
    }
  }, [assessmentOptions, formAssessmentId, formVarX, formVarY, needsVarY]);

  const clearForm = () => {
    setSelectedIndex(null);
    setFormType(defaultType);
    setFormDName('');
    setFormAssessmentId(defaultAssessment);
    setFormVarX('');
    setFormVarY('');
  };

  // Same rationale as ScheduleEventListEditor's applySelectedEvent: any mutation to the list
  // must leave selectedIndex pointing at a real item (never a stale/out-of-range one).
  const applySelectedItem = (index: number, item: StatsAggReportItemDefDto) => {
    setSelectedIndex(index);
    setFormType(item.Type);
    setFormDName(item.DName);
    setFormAssessmentId(item.AssessmentId);
    setFormVarX(item.VarX);
    setFormVarY(item.VarY);
  };

  const selectItem = (index: number) => {
    const item = items[index];
    if (!item) {
      return;
    }
    applySelectedItem(index, item);
  };

  // Keep a valid selection whenever the list is non-empty. The web listbox auto-highlights its
  // first option on its own, and a click on an already-highlighted option fires no onChange —
  // so leaving selectedIndex at null while items exist strands both the form (empty) and the
  // Update button (disabled) until some unrelated mutation happens to set a real index. This
  // also covers the initial mount after the parent hydrates a saved report's items (e.g. after
  // leaving and returning to the Aggregate tab).
  useEffect(() => {
    if (items.length === 0) {
      if (selectedIndex !== null) {
        clearForm();
      }
      return;
    }
    if (selectedIndex === null || selectedIndex >= items.length) {
      const nextIndex = selectedIndex === null ? 0 : items.length - 1;
      applySelectedItem(nextIndex, items[nextIndex]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Changing the assessment invalidates any variable picked from the previous one — reset the
  // Variable/Group By selections to the new assessment's first variable.
  const handleAssessmentChange = (assessmentId: string) => {
    setFormAssessmentId(assessmentId);
    const nextFirstVar = assessmentOptions.find((a) => a.value === assessmentId)?.aggVars[0]?.value ?? '';
    setFormVarX(nextFirstVar);
    setFormVarY(nextFirstVar);
  };

  const buildItemFromForm = (existingItemId?: string): StatsAggReportItemDefDto => ({
    ItemId: existingItemId ?? generateItemId(),
    Type: formType,
    DName: formDName,
    AssessmentId: formAssessmentId,
    VarX: formVarX || firstVar,
    VarY: needsVarY ? (formVarY || firstVar) : '',
    Pos: '',
  });

  const handleUpdate = () => {
    if (selectedIndex === null) {
      return;
    }
    const updated = buildItemFromForm(items[selectedIndex].ItemId);
    onChange(withRecomputedPos(items.map((item, i) => (i === selectedIndex ? updated : item))));
  };

  const handleAddNew = () => {
    const insertAt = selectedIndex !== null ? selectedIndex + 1 : items.length;
    const newItem = buildItemFromForm();
    const next = [...items];
    next.splice(insertAt, 0, newItem);
    const recomputed = withRecomputedPos(next);
    onChange(recomputed);
    applySelectedItem(insertAt, recomputed[insertAt]);
  };

  const handleRemove = () => {
    if (selectedIndex === null) {
      return;
    }
    const next = withRecomputedPos(items.filter((_, i) => i !== selectedIndex));
    onChange(next);
    if (next.length === 0) {
      clearForm();
      return;
    }
    const nextIndex = Math.min(selectedIndex, next.length - 1);
    applySelectedItem(nextIndex, next[nextIndex]);
  };

  const moveSelected = (direction: -1 | 1) => {
    if (selectedIndex === null) {
      return;
    }
    const targetIndex = selectedIndex + direction;
    if (targetIndex < 0 || targetIndex >= items.length) {
      return;
    }
    const next = [...items];
    const [moved] = next.splice(selectedIndex, 1);
    next.splice(targetIndex, 0, moved);
    const recomputed = withRecomputedPos(next);
    onChange(recomputed);
    setSelectedIndex(targetIndex);
  };

  const isMoveUpDisabled = selectedIndex === null || selectedIndex <= 0;
  const isMoveDownDisabled = selectedIndex === null || selectedIndex >= items.length - 1;

  return (
    <View style={styles.container}>
      <View style={styles.formCol}>
        <View style={styles.formRow}>
          <Text style={styles.formLabel}>Type</Text>
          <View style={styles.formInputWrap}>
            {renderFieldDropdown(formType, setFormType, AGG_ITEM_TYPE_OPTIONS, 'Item type')}
          </View>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>Title</Text>
          <TextInput
            style={styles.formInput}
            value={formDName}
            onChangeText={setFormDName}
            placeholder="Item title"
            placeholderTextColor={EDITOR_COLORS.placeholder}
          />
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>Assessment</Text>
          <View style={styles.formInputWrap}>
            {assessmentOptions.length === 0
              ? <Text style={styles.formStaticValue}>No enabled assessments</Text>
              : renderFieldDropdown(formAssessmentId, handleAssessmentChange, assessmentOptions, 'Assessment')}
          </View>
        </View>

        <View style={styles.formRow}>
          <Text style={styles.formLabel}>Variable</Text>
          <View style={styles.formInputWrap}>
            {variableOptions.length === 0
              ? <Text style={styles.formStaticValue}>Pick an assessment first</Text>
              : renderFieldDropdown(formVarX, setFormVarX, variableOptions, 'Variable')}
          </View>
        </View>

        {needsVarY ? (
          <View style={styles.formRow}>
            <Text style={styles.formLabel}>Group By</Text>
            <View style={styles.formInputWrap}>
              {variableOptions.length === 0
                ? <Text style={styles.formStaticValue}>Pick an assessment first</Text>
                : renderFieldDropdown(formVarY, setFormVarY, variableOptions, 'Group by variable')}
            </View>
          </View>
        ) : null}
      </View>

      <View style={styles.actionsCol}>
        <Pressable
          style={[styles.actionBtn, styles.actionBtnPrimary, selectedIndex === null && styles.actionBtnDisabled]}
          onPress={handleUpdate}
          disabled={selectedIndex === null}
        >
          <Text style={styles.actionBtnText}>Update {'>>'}</Text>
        </Pressable>

        <View style={styles.actionsSpacer} />

        <Pressable style={[styles.actionBtn, styles.actionBtnPrimary]} onPress={handleAddNew}>
          <Text style={styles.actionBtnText}>Add New {'>>'}</Text>
        </Pressable>

        <Pressable
          style={[styles.actionBtn, styles.actionBtnPrimary, selectedIndex === null && styles.actionBtnDisabled]}
          onPress={handleRemove}
          disabled={selectedIndex === null}
        >
          <Text style={styles.actionBtnText}>{'<<'} Remove</Text>
        </Pressable>
      </View>

      <View style={styles.listCol}>
        {Platform.OS === 'web' ? (
          React.createElement(
            'select' as any,
            {
              size: LISTBOX_VISIBLE_ROWS,
              value: selectedIndex !== null ? String(selectedIndex) : '',
              onChange: (e: any) => {
                const idx = parseInt(e?.target?.value ?? '', 10);
                if (!Number.isNaN(idx)) {
                  selectItem(idx);
                }
              },
              style: WEB_LISTBOX_STYLE,
              'aria-label': 'Report items',
            },
            items.map((item, index) => React.createElement('option' as any, { key: item.ItemId, value: String(index) }, itemListLabel(item, index)))
          )
        ) : (
          <View style={styles.nativeListbox}>
            {items.map((item, index) => (
              <Pressable
                key={item.ItemId}
                style={[styles.nativeListItem, selectedIndex === index && styles.nativeListItemSelected]}
                onPress={() => selectItem(index)}
              >
                <Text style={styles.nativeListItemText} numberOfLines={1}>{itemListLabel(item, index)}</Text>
              </Pressable>
            ))}
          </View>
        )}
      </View>

      <View style={styles.orderCol}>
        <Pressable style={[styles.actionBtn, styles.actionBtnOrder, isMoveUpDisabled && styles.actionBtnDisabled]} onPress={() => moveSelected(-1)} disabled={isMoveUpDisabled}>
          <Text style={styles.actionBtnText}>Move Up</Text>
        </Pressable>
        <Pressable style={[styles.actionBtn, styles.actionBtnOrder, isMoveDownDisabled && styles.actionBtnDisabled]} onPress={() => moveSelected(1)} disabled={isMoveDownDisabled}>
          <Text style={styles.actionBtnText}>Move Down</Text>
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
    gap: 10,
  },
  formCol: {
    flex: 1,
    minWidth: 260,
    gap: 6,
  },
  formRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  formLabel: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    width: 80,
  },
  formInput: {
    ...editorText,
    flex: 1,
    height: 24,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
  },
  formInputWrap: {},
  formStaticValue: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  actionsCol: {
    width: 92,
    justifyContent: 'center',
    gap: 6,
  },
  actionsSpacer: {
    height: 10,
  },
  listCol: {
    width: LISTBOX_WIDTH + 10,
    justifyContent: 'center',
  },
  orderCol: {
    width: 98,
    justifyContent: 'center',
    gap: 6,
  },
  nativeListbox: {
    width: LISTBOX_WIDTH,
    height: LISTBOX_HEIGHT,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 4,
    backgroundColor: EDITOR_COLORS.background,
  },
  nativeListItem: {
    minHeight: LISTBOX_ROW_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 8,
    paddingVertical: 1,
  },
  nativeListItemSelected: {
    backgroundColor: '#eff6ff',
  },
  nativeListItemText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  actionBtn: {
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  actionBtnPrimary: {
    width: 86,
  },
  actionBtnOrder: {
    width: 92,
  },
  actionBtnDisabled: {
    opacity: 0.5,
  },
  actionBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
    textAlign: 'center',
  },
});
